import { I18n } from 'react-redux-i18n';
import HotelTypes from '../data/hotel_types.json';
import Locales from '../i18n';
import moment from 'moment';

const { REACT_APP_DNS_API_CHEST } = process.env
const locales = Object.keys(Locales);
const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json' };

/**
 * Returns a object containing the main values of the location such as
 * the session of the user, the env, and the provider.
 *
 * @param {Object} location Object containing the location info.
 */
export function getURLQuery(location) {
    const query = new URLSearchParams(location.search);
    return {
        session: query.get('s'),
        back: query.get('back') || '',
        env: query.get('e'),
        lang: query.get('lang') || 'en',
        provider: location.pathname.split('/')[1]
    }
}

export function getFormatDate(date) {
    try {
        if (!date) return date;
        if (typeof date === 'string') {
            if (date.indexOf('-') > -1) {
                const dateObject = new Date(date);

                const dateString = ('0' + dateObject.getDate()).slice(-2) +
                    '/' + ('0' + (dateObject.getMonth() + 1)).slice(-2) +
                    '/' + dateObject.getFullYear();

                return dateString;
            } else {
                return date;
            }
        }

        const day = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
        const month = date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1;

        return `${day}/${month}/${date.getFullYear()}`;
    } catch (e) {
        console.error('FORMAT ERROR ', e);
        return date.toString();
    }
}

/**
 * Returns a date in the format YYYY-MM-DD.
 *
 * @param {*} date The date to format.
 */
export function sendFormatDate(date) {
    if (typeof date !== 'string') return date;
    date = date.split('/');
    return `${date[2]}-${date[1]}-${date[0]}`;
}

/**
 * Check if email is valid or not.
 *
 * @param {String} email Email string to test.
 * @returns {Boolean} True or false.
 */
export function isValidEmail(email) {
    // eslint-disable-next-line
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
}

/**
 * Returns the time (HH:MM) given a date.
 *
 * @param date Date from where to get the time.
 */
export function getDateTime(date) {
    return (/[0-9]{2}:[0-9]{2}/.exec(date)[0]);
}

/**
 * Given a date, it returns the type of passenger in the current lang.
 *
 * @param params.birthdate Birthdate of the passenger.
 * @param params.key False to return string in the current lang.
 * @returns Adult, Child or Infant
 */
export function getPassengerType({ birthdate, key = false }) {
    const date = moment(birthdate)
    const adultDate = moment().subtract(16, 'years')
    const babyDate = moment().subtract(1, 'years')

    if (date.isBefore(adultDate))
        return key ? 'Adult' : I18n.t('info.adult');

    else if (date.isAfter(babyDate))
        return key ? 'Infant' : I18n.t('info.infant');

    else
        return key ? 'Child' : I18n.t('info.child');
}

export function getShortDate(date) {
    if (typeof date === 'string') {
        if (date !== '') {
            let dateExec = /[0-9]{4}-[0-9]{2}-[0-9]{2}/.exec(date)[0];
            date = new Date(dateExec);
        } else {
            date = new Date();
        }
    }

    return `${I18n.t('weekday.' + date.getDay())} ${date.getDate()} ${I18n.t('month.' + date.getMonth())}`;
}

export function getDayMonth(date) {
    if (typeof date === 'string') {
        date = date.split('/');
        date = new Date(`${date[2]}-${date[1]}-${date[0]}`);
    }
    return `${date.getDate()} ${I18n.t('month.' + date.getMonth())}`;
}

export function getDayName(date) {
    if (typeof date === 'string') {
        date = date.split('/');
        date = new Date(`${date[2]}-${date[1]}-${date[0]}`);
    }

    return `${I18n.t('weekday.' + date.getDay())}`;
}

export function showPassengers(query, showDot = false) {
    let passengers = ``;

    if (query.adults || query.children || query.infants || query.children) {
        passengers = `${showDot ? '. ' : ''}${query.adults} ${query.adults > 1 ? I18n.t('adults') : I18n.t('adult')}`;

        if (query.children && query.infants) {
            passengers += ` ${query.children} ${(query.children > 1 ? I18n.t('children') : I18n.t('children'))}`;
            passengers += ` ${I18n.t('and')} ${query.infants} ${(query.infants > 1 ? I18n.t('infants') : I18n.t('infant'))}`;
        } else if (query.children) {
            passengers += ` ${I18n.t('and')} ${query.children} ${(query.children > 1 ? I18n.t('children') : I18n.t('children'))}`;
        } else if (query.children) {
            passengers += ` ${I18n.t('and')} ${query.children} ${(query.children > 1 ? I18n.t('children') : I18n.t('children'))}`;
        } else if (query.infants) {
            passengers += ` ${I18n.t('and')} ${query.infants} ${(query.infants > 1 ? I18n.t('infants') : I18n.t('infant'))}`;
        }
    } else {
        passengers = `. ${query.passengers} ${query.passengers > 1 ? I18n.t('passengers') : I18n.t('passenger')}`;
    }

    return passengers;
}

/**
 * Closes the window using a different method depending on the user channel.
 *
 * @param {Object} channel Channel of the user
 * @param {Object} args Args for the method to use for close windows
 * @param {String} args.env The environment
 * @param {String} args.back The phone to return to (whatsapp only)
 */
function closeWindows(channel, args = {}) {
    switch (channel.name) {
        case 'ios':
        case 'android':
        case 'gmail':
        case 'imap':
        case 'imap_yahoo':
        case 'imap_icloud':
        case 'office360':
        case 'outlook':
        case 'exchange':
            window.parent.postMessage('close', '*');
            break;
        case 'chatweb':
            window.parent.postMessage('close', 'https://chatweb.nixi1.com');
            break;
        case 'twilio':
            window.open('sms:+' + channel.id, '_self');
            break;
        case 'whatsapp':
            window.open('https://wa.me/' + channel.id, '_self');
            break;
        case 'telegram':
            window.location.href = 'https://t.me/' + channel.id;
            break;
        case 'facebook':
            window.location.replace('https://m.me/' + channel.id);
            break;
        default:
            window.close();
            break;
    }
}

/**
 * Check if the device from where is accessed is a mobile.
 *
 * @returns {Boolean} True or false.
 */
export function isMobile() {
    // eslint-disable-next-line
    return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4)) ? true : false;
}

export function getUrlParam(name) {
    const url = window.location.href;
    // eslint-disable-next-line
    name = name.replace(/[\[\]]/g, "\\$&");
    const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, " "));
}

/**
 * Returns the locale in the iso2 format. (es, en..)
 *
 * @param {String} locale The locale string.
 */
export function checkLocale(locale) {
    if (!locale) return 'es';
    locale = locale.split("_")[0].toLowerCase();
    if (locales.indexOf(locale) === -1) return 'en';
    return locale;
}

export function getHotelFacilities(locale) {
    let language;
    if (!locale) {
        language = 'en';
    } else {
        locale = locale.split('_')[0].toLowerCase();
        if (locales.indexOf(locale) === -1) language = 'en';
        else language = locale;
    }

    const hotel_types = HotelTypes.result.map((facility) => {
        let facility_type = {};
        facility_type.hotel_facility_type_id = facility.hotel_facility_type_id;
        if (language !== 'en') {
            let translation = facility.translations.filter((trans) => {
                return true;
                //if (trans.language === language) return true;
            });
            facility_type.name = translation[0].name;
        } else {
            facility_type.name = facility.name
        }
        return facility_type;
    });

    return hotel_types;
}

/**
 * Returns the URL of the Chest API microservice
 * depending on the window host.
 */
export function getChestApiUrl() {
    return REACT_APP_DNS_API_CHEST || "http://localhost:3334"

}

export function Utf8ArrayToStr(array) {
    let out, i, len, c;
    let char2, char3;

    out = "";
    len = array.length;
    i = 0;
    while (i < len) {
        c = array[i++];
        switch (c >> 4) {
            case 0: case 1: case 2: case 3: case 4: case 5: case 6: case 7:
                // 0xxxxxxx
                out += String.fromCharCode(c);
                break;
            case 12: case 13:
                // 110x xxxx   10xx xxxx
                char2 = array[i++];
                out += String.fromCharCode(((c & 0x1F) << 6) | (char2 & 0x3F));
                break;
            case 14:
                // 1110 xxxx  10xx xxxx  10xx xxxx
                char2 = array[i++];
                char3 = array[i++];
                out += String.fromCharCode(((c & 0x0F) << 12) |
                    ((char2 & 0x3F) << 6) |
                    ((char3 & 0x3F) << 0));
                break;
            default:
                return;
        }
    }

    return out;
}

/**
 * Given a number of minutes, it returs an hour in HH:MM format.
 * @param {Number} num Minutes of the day, between 0 and 1439.
 */
export function minutesToTimeString(num) {
    let hours = Math.floor(num / 60);
    let minutes = num % 60;

    if (hours < 10) hours = "0" + hours;
    if (minutes < 10) minutes = "0" + minutes;

    let timeString = `${hours}:${minutes}`;

    return timeString;
}


/**
 * Given a string in format HH:MM it returs the number of minutes.
 * @param {String} hour
 */
export function hourStringToMinutes(hour) {
    if (!hour) return null;
    let [hours, minutes] = hour.split(':');
    let totalMinutes = (parseInt(hours, 10) * 60) + parseInt(minutes, 10);
    return totalMinutes;
}

/**
 * Returns a string of the total travel time in format XXh YYm
 * @param travelTime Total travel time in minutes
 * @returns String
 */
export function getFormatTravelTime(travelTime) {
    const totalTimeInHours = (travelTime / 60).toFixed(2);
    const totalHours = Math.floor(totalTimeInHours);
    const totalMinutesLeft = Math.round((totalTimeInHours - totalHours) * 60);

    return (totalHours + "h") + ((totalMinutesLeft > 0) ? (' ' + totalMinutesLeft + 'm') : '');
}

/**
 * Returns the time between two hours in minutes.
 * @param startingHour The starting hour
 * @param endingHour The end hour
 * @param timeZoneHours Timezone changing hours
 */
export function calculateTimeBetweenHours(startingHour, endingHour, timeZoneHours = 0) {

    startingHour = startingHour.split(':');
    endingHour = endingHour.split(':');

    const startingDate = (new Date()).setHours(parseInt(startingHour[0], 10), parseInt(startingHour[1], 10));
    let endingDate = (new Date()).setHours(parseInt(endingHour[0], 10), parseInt(endingHour[1], 10));

    if (startingDate > endingDate) {
        endingDate = new Date(endingDate);
        let endingDateDay = endingDate.getDate() + 1;
        endingDate.setDate(endingDateDay);
    }

    return Math.abs(((startingDate - endingDate) / 1000 / 60) + timeZoneHours * 60);
}

/**
 * Generates a url for redirection starting the chatbot with the offer ID or query string
 * @param {String} provider Provider from where the client comes
 * @param {String} payload Message to send to bot treated as payload
 * @returns {Array} Array containing the url generated and method to open with
 */
function generateChannelUrl(channel, payload) {
    switch (channel.name) {
        case 'whatsapp':
            return [`https://wa.me/${channel.id}?text=share%20${payload}`, 'replace']
        case 'telegram':
            return [`http://telegram.me/${channel.id}?start=${payload}`, ''];
        case 'facebook':
            return [`https://m.me/${channel.id}?ref=share%20${payload}`, 'replace'];
        case 'twilio':
            return [`sms://+${channel.id}&body=share%20${payload}`, 'open'];
        default:
            console.error('Error, webview channel not found.');
            return;
    }
}

/**
 * Open url with a given method. Pure javascript.
 * @param {String} url The url that will be opened
 * @param {String} method Named string of the method
 */
export function openUrlWithMethod(url, method) {
    switch (method) {
        case 'open':
            window.open(url, '_self');
            break;
        case 'open-blank':
            window.open(url, '_blank');
            break;
        case 'replace':
            window.location.replace(url);
            break;
        default:
            window.location.href = url;
            break;
    }
}

/**
 * Redirects the user to the chatbot and sends a message to book an offer.
 * @param {Object} offerData The data of the offer user has selected.
 * @param {String} productType Flight or Hotel.
 * @param {String} provider The provider from when the user comes.
 * @param {Boolean} expired Offer is expired.
 */
export async function startChatbotWithOffer(offerData, productType, provider, configuration, expired = false) {
    // To do:
    // - show popup?
    // - Open bot with other offer?
    if (expired) return console.log('Offer expired');

    return fetch(`${getChestApiUrl()}/chestApi/share`, {
        method: 'POST',
        headers,
        body: JSON.stringify(offerData)
    })
        .then(r => r.json())
        .then(res => {
            const generatedUrl = generateChannelUrl({ id: configuration.channels[provider].botId, name: provider }, `ID_${productType[0]}_${res.token}`);
            const [url, method] = generatedUrl;
            return openUrlWithMethod(url, method);
        });
}

/**
 * Redirects the user to the chatbot and sends a message to start the bot.
 * @param {String} provider The provider from when the user comes.
 * @param {Object} configuration The configuration of the company.
 */
export async function startChatbot(provider, configuration) {
    const generatedUrl = generateChannelUrl({ id: configuration.channels[provider].botId, name: provider }, 'hi');
    const [url, method] = generatedUrl;
    return openUrlWithMethod(url, method);
}

/**
 * Returns the current host.
 */
export function getHost() {
    const url = window.location.href.split("/");
    return `${url[0]}//${url[2]}`;
}

/**
 * Returns a two character string with the initals of the given provider.
 * @param {String} provider Provider full name
 */
export function getProviderInitials(provider) {
    switch (provider) {
        case 'SKYSCANNER': return 'SK';
        case 'TRAVELFUSION': return 'TF';
        case 'AERTICKET': return 'AT';
        case 'HOTELBEDS': return 'HB';
        case 'HITCHHIKER': return 'HH';
        case 'HOTELSCOMBINED': return 'HC';
        case 'BOOKINGCOM': return 'BK';
        case 'WASABI_HOTELS': return 'WS';
        case 'AMADEUS': return 'AM';
        case 'AMADEUS_HOTELS': return 'AM';
        case 'TRAVELLOOP_HOTELS': return 'TL';

        default: return 'IDK';
    }
}

/**
 * Returns a string of a font-awesome icon representing
 * a sorting criteria.
 * @param {String} sort The order of the results
 */
export function getSortIconClass(sort) {
    const [type] = sort.split(':');
    switch (type) {
        case 'score': return 'trophy';
        case 'stars': return 'star';
        case 'time': return 'hourglass';
        case 'outboundTime': return 'history';
        case 'inboundTime': return 'history';
        case 'airline': return 'plane';
        case 'stops': return 'dot-circle';
        case 'price':
        default: return 'money-bill-alt';
    }
}

/**
 * Returna a string of a font-awesome icon representing a
 * passenger type.
 * @param {String} passengerType The passenger type
 * @param {String} gender Gender of the passenger
 */
export function getPassengerIconClass(passengerType, gender) {
    switch (passengerType.toLowerCase()) {
        case 'adult': return gender.toLowerCase();
        case 'child': return 'child';
        case 'infant': return 'baby';
        default: return 'user';
    }
}

/**
 * Returns to chat or close window given the url parameters.
 * @param {Object} params.urlQuery The url query parameters used to close the windows properly
 * @param {Object} params.configuration The configuration of the company
 */
export function returnToChat(params) {
    const urlQuery = params.urlQuery
    const channels = params.configuration.channels
    const { provider, env, back } = urlQuery;
    return closeWindows({ name: provider, id: channels[provider].botId }, { env, back })
}

/**
 * Returns an object with cleaned fields based on type.
 *
 * @param {Object} params Params to pass
 * @param {Object} params.object Object from where to clean fields
 * @param {String} params.type Type of object based on containing fields
 */
export function removeObjectFields(params) {
    const object = params.object;
    const type = params.type;

    switch (type) {
        case 'address':
            delete object.errors;
            delete object.emailConfirm;
            delete object.user;
            break;
        case 'passenger':
            object.map((passenger, index) => {
                if (!index) passenger.owner = true;

                passenger.birthdate = sendFormatDate(getFormatDate(passenger.birthdate));

                // clean fields
                delete passenger.errors;

                if (!passenger.expirationDate)
                    delete passenger.expirationDate;
                else
                    passenger.expirationDate = sendFormatDate(getFormatDate(passenger.expirationDate));

                if (!passenger.issueDate)
                    delete passenger.issueDate;
                else
                    passenger.issueDate = sendFormatDate(getFormatDate(passenger.issueDate));

                if (!passenger.specialdiscounts)
                    delete passenger.specialdiscounts;

                if (!passenger.municipality)
                    delete passenger.municipality;

                if (!passenger.municipality_zip)
                    delete passenger.municipality_zip;

                return passenger;
            }).filter(p => (p.position >= object.totalPassengers) ? false : true);
            break;
        default:
            return object;
    }
    return object;
}


/**
 * Returns true or false if day is not in the desired age range.
 *
 * @param {String} type Passenger type. Adult | Child | Infant
 * @param {MomentObject} day Day to check
 */
export const disabledDayByPassengerType = (type, day) => {

    if (day.isAfter(moment())) return true

    if (!type) return false

    const adultDate = moment().subtract(18, 'years')
    const childDate = moment().subtract(11, 'years')

    switch (type) {
        case 'Adult': {
            return !day.isBefore(adultDate)
        }
        case 'Child': {
            return !day.isAfter(adultDate)
        }
        case 'Infant': {
            return !day.isAfter(childDate)
        }
        default: {
            return false
        }
    }
}



export const alphabeticalSort = (a, b) => a > b ? 1 : a < b ? -1 : 0;

export const alphabeticalSortByName = (a, b) => alphabeticalSort(a.name.toLowerCase(), b.name.toLowerCase())

export const matchesPassengerCond = conditions => passenger => {

    if (conditions.passengers.indexOf(passenger.passport) !== -1) return false

    if (conditions.type === 'Adult') return true

    if (passenger.type !== conditions.type) return false

    // if (!passenger.isResident && conditions.specialdiscounts === 'SpainIslandsResidentialDiscount') return false
    if (!passenger.isResident && conditions.specialdiscounts === 'spanishResident') return false

    // For children, when searching by date
    // if (passenger.birthdate !== conditions.birthdate) return false

    return true
}
